import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cacTram, linkTram, linkViec } from "../data/lo-trinh.js";
import { readWorkspace } from "../lib/workspace.js";
import { docGoiY, KHOA_NGANH } from "../lib/goiYTram.js";
import { trangThaiViec } from "./TienDoLoTrinh.jsx";
import { track } from "../lib/track.js";

/**
 * Bản đồ 5 trạm — phiên bản trực quan của /lo-trinh.
 *
 * Đọc thẳng TRAM trong lo-trinh.js (ten, cauHoi, mangVe, viec, bieuTuong,
 * ketQuaNgan), nên không có lộ trình thứ hai để giữ cho khớp.
 *
 * Ba chế độ, chọn sau khi mount (HTML prerender giống nhau cho mọi người):
 *   - "tien-do": người dùng đã có kết quả ở ít nhất một việc → trạm sáng theo
 *     tiến độ thật (cùng hàm trangThaiViec với trang Bản chuẩn bị), cờ "Bạn"
 *     đứng ở trạm đầu tiên chưa xong. Không tự chạy.
 *   - "goi-y": chưa có tiến độ nhưng đã chấm Điểm sẵn sàng → cờ đứng ở trạm
 *     được gợi ý. Không tự chạy.
 *   - "vi-du": còn lại → tự đi một vòng 5 trạm để người mới thấy mỗi trạm mang
 *     về kết quả gì. Bấm vào trạm bất kỳ thì dừng.
 * Người bật "giảm chuyển động" không bao giờ thấy tự chạy.
 *
 * Mỗi trạm có ba trạng thái hiển thị: đã qua (nét liền, xanh), đang ở (vòng
 * sáng, cờ), chưa tới (nét chấm, mờ). Thẻ kết quả theo đúng ba trạng thái đó.
 *
 * onChonTram (tuỳ chọn): trang /lo-trinh truyền vào để nút ở khung chi tiết
 * mở <details> của trạm ngay bên dưới thay vì chuyển trang.
 */

const BIEU_TUONG = {
  "dong-ho": <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M9.5 3h5" /></>,
  "bieu-do": <><path d="M4 20V4M4 20h16" /><path d="M8 16v-4M12 16V8M16 16v-6" /></>,
  "co-phan": <><path d="M12 3a9 9 0 1 0 9 9h-9z" /><path d="M15 3.5A9 9 0 0 1 20.5 9H15z" /></>,
  "trinh-chieu": <><path d="M4 4h16v11H4z" /><path d="M12 15v5M8 20h8M8 11.5l3-3 2 2 3-3" /></>,
  gui: <><path d="M21 3L10 14" /><path d="M21 3l-7 18-4-7-7-4z" /></>,
};

/** Hình nhỏ trong thẻ kết quả — vẽ đúng thứ founder sẽ có trong tay. */
const HINH = {
  "dong-ho": (
    <>
      <rect x="4" y="16" width="76" height="14" rx="3" className="bd-h-vien" />
      <rect x="4" y="16" width="46" height="14" rx="3" className="bd-h-xanh" />
      <path d="M50 10v26" className="bd-h-net" />
      <text x="42" y="44" className="bd-h-chu" textAnchor="middle">18 tháng</text>
    </>
  ),
  "bieu-do": (
    <>
      <rect x="4" y="4" width="76" height="40" rx="3" className="bd-h-vien" />
      {[17, 30].map((y) => <path key={y} d={`M4 ${y}h76`} className="bd-h-net mo" />)}
      {[29, 54].map((x) => <path key={x} d={`M${x} 4v40`} className="bd-h-net mo" />)}
      <path d="M58 38l6-8 6 3 6-12" className="bd-h-duong" />
    </>
  ),
  "co-phan": (
    <>
      <circle cx="30" cy="24" r="18" className="bd-h-vien" />
      <path d="M30 24V6a18 18 0 0 1 15.6 27z" className="bd-h-xanh" />
      {[12, 22, 32].map((y, i) => <path key={y} d={`M58 ${y}h${20 - i * 5}`} className="bd-h-net" />)}
    </>
  ),
  "trinh-chieu": (
    <>
      <rect x="4" y="6" width="52" height="34" rx="3" className="bd-h-vien" />
      <path d="M12 32l10-10 8 6 16-14" className="bd-h-duong" />
      <circle cx="68" cy="23" r="12" className="bd-h-xanh" />
      <path d="M63 23l3.5 3.5L73 19" className="bd-h-dau" />
    </>
  ),
  gui: (
    <>
      {[4, 19, 34].map((y, i) => (
        <g key={y}>
          <rect x="4" y={y} width="76" height="11" rx="3" className="bd-h-vien" />
          <path d={`M9 ${y + 5.5}l2.5 2.5 4-5`} className="bd-h-duong" />
          <path d={`M22 ${y + 5.5}h${44 - i * 10}`} className="bd-h-net" />
        </g>
      ))}
    </>
  ),
};

const X = (i) => 100 + i * 200;
const Y = (i) => (i % 2 ? 92 : 46);
const DAY_DUOI = 150; // đáy SVG, nơi dây nối gặp thẻ kết quả

function Icon({ ten, ...rest }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>
      {BIEU_TUONG[ten]}
    </svg>
  );
}

const NHAN_THE = { "da-co": "Đã có", dang: "Đang làm", chua: "Chưa có" };

export default function BanDoLoTrinh({ onChonTram }) {
  const ds = cacTram();
  const n = ds.length;
  const [cur, setCur] = useState(0);
  const [cheDo, setCheDo] = useState("vi-du");
  const [tienDo, setTienDo] = useState(null); // ["xong" | "dang-lam" | "chua"] theo trạm
  const [chay, setChay] = useState(false);
  const [nganh, setNganh] = useState("");

  useEffect(() => {
    let td = null;
    try {
      const d = readWorkspace();
      const arr = ds.map((t) => {
        const tt = t.viec.map((v, i) => trangThaiViec(d, t, v, i));
        if (tt.length && tt.every((x) => x === "xong")) return "xong";
        return tt.some((x) => x !== "chua") ? "dang-lam" : "chua";
      });
      if (arr.some((x) => x !== "chua")) td = arr;
    } catch { /* bộ nhớ trình duyệt bị chặn: dùng chế độ ví dụ */ }
    try { setNganh(window.localStorage.getItem(KHOA_NGANH) || ""); } catch { /* chế độ riêng tư */ }

    const giam = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const g = docGoiY();
    if (td) {
      const i = td.findIndex((x) => x !== "xong");
      setTienDo(td);
      setCheDo("tien-do");
      setCur(i < 0 ? n - 1 : i);
    } else if (g?.tram && ds.some((t) => t.so === g.tram)) {
      setCheDo("goi-y");
      setCur(ds.findIndex((t) => t.so === g.tram));
    } else if (!giam) {
      setChay(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tự chạy: dừng lâu hơn ở trạm cuối rồi quay về đầu.
  useEffect(() => {
    if (!chay) return undefined;
    const id = setTimeout(() => setCur((c) => (c === n - 1 ? 0 : c + 1)), cur === n - 1 ? 4200 : 2600);
    return () => clearTimeout(id);
  }, [chay, cur, n]);

  // Không chạy khi tab bị ẩn: tránh nhảy cóc nhiều trạm lúc người dùng quay lại.
  useEffect(() => {
    if (cheDo !== "vi-du") return undefined;
    const doi = () => { if (document.hidden) setChay(false); };
    document.addEventListener("visibilitychange", doi);
    return () => document.removeEventListener("visibilitychange", doi);
  }, [cheDo]);

  const chon = (i, cach) => {
    const moi = (i + n) % n;
    setCur(moi);
    setChay(false);
    track("ban_do_chon_tram", { tram: ds[moi].id, method: cach });
  };

  // Trạng thái trạm và thẻ kết quả.
  const ttTram = (i) => {
    if (tienDo) {
      if (i === cur) return "dang";
      return tienDo[i] === "xong" ? "xong" : tienDo[i] === "dang-lam" ? "dang-lam" : "chua";
    }
    return i < cur ? "xong" : i === cur ? "dang" : "chua";
  };
  const ttThe = (i) => {
    // "da-co", không phải "co": styles.css đã dùng .co cho khung cảnh báo.
    if (tienDo) return tienDo[i] === "xong" ? "da-co" : tienDo[i] === "dang-lam" || i === cur ? "dang" : "chua";
    return i < cur ? "da-co" : i === cur ? "dang" : "chua";
  };
  const doanXong = (i) => (tienDo ? tienDo[i] === "xong" : i < cur);
  const soXong = tienDo ? tienDo.filter((x) => x === "xong").length : cur;

  const tram = ds[cur];
  const nhanDau = {
    "vi-du": `Ví dụ một vòng ${n} trạm`,
    "tien-do": "Tiến độ của bạn",
    "goi-y": "Gợi ý từ lần chấm Điểm sẵn sàng",
  }[cheDo];
  const coCo = cheDo !== "vi-du"; // cờ có chữ "Bạn" khi gắn với người dùng thật

  return (
    <div className="bd" data-che-do={cheDo}>
      <div className="bd-ban">
        <div className="bd-dau">
          <p className="bd-nhan-nho">{nhanDau}</p>
          <div className="bd-dieu-khien">
            <span className="bd-dem">Đã qua <b>{soXong}/{n}</b> trạm</span>
            <button type="button" className="bd-nut-nho" onClick={() => chon(cur - 1, "nut")} aria-label="Trạm trước">←</button>
            {cheDo === "vi-du" && (
              <button type="button" className="bd-nut-nho" onClick={() => setChay((c) => !c)} aria-pressed={chay}>
                {chay ? "Tạm dừng" : "Tự chạy"}
              </button>
            )}
            <button type="button" className="bd-nut-nho" onClick={() => chon(cur + 1, "nut")} aria-label="Trạm sau">→</button>
          </div>
        </div>

        {/* Ngang: từ 760px. Nhãn và thẻ là lưới HTML 5 cột, chia đúng như toạ độ
            X của SVG, nên chữ dài vẫn xuống dòng tự nhiên. */}
        <div className="bd-ngang">
          <div className="bd-cot5">
            {ds.map((t, i) => (
              <button
                type="button"
                key={t.id}
                className={`bd-tram-nut ${ttTram(i)}`}
                aria-current={i === cur ? "step" : undefined}
                onClick={() => chon(i, "nhan")}
              >
                <span className="so">Trạm {t.so}</span>
                <span className="ten">{t.ten}</span>
              </button>
            ))}
          </div>

          <svg className="bd-duong" viewBox={`0 0 1000 ${DAY_DUOI}`} aria-hidden="true">
            <path className="bd-doan xong" d={`M0 ${Y(0) + 26} L${X(0) - 28} ${Y(0) + 6}`} />
            {ds.slice(0, -1).map((t, i) => (
              <path key={t.id} className={`bd-doan ${doanXong(i) ? "xong" : "chua"}`} d={`M${X(i)} ${Y(i)} L${X(i + 1)} ${Y(i + 1)}`} />
            ))}
            {ds.map((t, i) => (
              <path key={`d${t.id}`} className={`bd-day ${ttThe(i)}`} d={`M${X(i)} ${Y(i) + 30} V${DAY_DUOI}`} />
            ))}
            {ds.map((t, i) => (
              <g key={`n${t.id}`} className={`bd-nut ${ttTram(i)}`} onClick={() => chon(i, "nut-tron")}>
                <circle className="bd-hao" cx={X(i)} cy={Y(i)} r="27" />
                <circle className="bd-vong" cx={X(i)} cy={Y(i)} r="26" />
                <g className="bd-icon" transform={`translate(${X(i) - 12} ${Y(i) - 12})`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {BIEU_TUONG[t.bieuTuong]}
                </g>
              </g>
            ))}
            <g className="bd-co" style={{ transform: `translate(${X(cur) + 18}px, ${Y(cur) - 14}px)` }}>
              <path className="can" d="M0 0V-34" />
              <path className="la" d={coCo ? "M0 -34h34l-6 8 6 8H0z" : "M0 -34h18l-5 6 5 6H0z"} />
              {coCo && <text x="4" y="-22.5">BẠN</text>}
            </g>
          </svg>

          <div className="bd-cot5 bd-the">
            {ds.map((t, i) => (
              <button type="button" key={t.id} className={`bd-ket-qua ${ttThe(i)}`} onClick={() => chon(i, "the")}>
                <svg viewBox="0 0 84 48" aria-hidden="true">{HINH[t.bieuTuong]}</svg>
                <span className="tieu-de">{t.ketQuaNgan}</span>
                <span className="tt">{ttThe(i) === "chua" && !tienDo ? `Có sau trạm ${t.so}` : NHAN_THE[ttThe(i)]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Dọc: điện thoại. */}
        <ol className="bd-doc">
          {ds.map((t, i) => (
            <li key={t.id} className={`bd-doc-tram ${ttTram(i)}`}>
              <button type="button" onClick={() => chon(i, "doc")} aria-current={i === cur ? "step" : undefined}>
                <span className="bd-doc-cham"><Icon ten={t.bieuTuong} /></span>
                <span className="bd-doc-than">
                  <span className="so">Trạm {t.so}</span>
                  <span className="ten">{t.ten}</span>
                  <span className={`bd-doc-kq ${ttThe(i)}`}>{t.ketQuaNgan}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div className="bd-chi-tiet" aria-live={chay ? "off" : "polite"}>
        <div className="bd-hien" key={tram.id}>
          <p className="eyebrow">Trạm {tram.so}/{n}: {tram.ten}</p>
          {/* Điện thoại chỉ giữ "Kết quả cần có" và nút: câu hỏi và danh sách việc
              ẩn bằng CSS (.bd-chi-tiet-day), đã có đủ ở trang Lộ trình. */}
          <h3 className="bd-chi-tiet-day">{tram.cauHoi}</h3>
          <p className="bd-mang-ve"><strong>Kết quả cần có:</strong> {tram.mangVe}</p>
          <ol className="bd-viec bd-chi-tiet-day">
            {tram.viec.slice(0, 3).map((v) => (
              <li key={v.ten}>
                <span>{v.ten}</span>
                <Link to={linkViec(tram, v, nganh)} onClick={() => track("bam_viec", { tram: tram.id, nguon: "ban_do" })}>
                  {v.nhan} →
                </Link>
              </li>
            ))}
          </ol>
          {onChonTram ? (
            <button type="button" className="nut phu" onClick={() => onChonTram(tram)}>
              Xem đủ {tram.viec.length} việc của Trạm {tram.so} ↓
            </button>
          ) : (
            <Link className="nut phu" to={linkTram(tram)}>
              Xem đủ {tram.viec.length} việc của Trạm {tram.so} →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
